import { Box, Container, Paper, Typography, Divider, Alert, List, ListItem, ListItemIcon, ListItemText, Chip } from "@mui/material";
import { CheckCircle, Warning, Info, Schedule, WhatsApp, Receipt, Person, Email } from "@mui/icons-material";

export const GuidePage = () => {
  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Guía de Facturación
          </Typography>
          <Chip label="BETA" color="warning" size="small" />
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Hospital Veterinario Peninsular — Sistema de facturación en línea. Pruebas iniciadas el 23 de marzo de 2026.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Este sistema está en fase beta. Si encuentras algún problema, contacta por WhatsApp al +52 999 184 1434.
        </Alert>

        <Divider sx={{ mb: 3 }} />

        {/* CÓMO FUNCIONA */}
        <Typography variant="h5" gutterBottom fontWeight="bold">¿Cómo funciona?</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Este portal te permite solicitar facturas (CFDI) de tus visitas al hospital veterinario. El proceso es sencillo:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><Person color="primary" /></ListItemIcon>
            <ListItemText primary="1. Regístrate con tu RFC, tu correo y tus datos fiscales" secondary="Te enviamos un enlace a tu correo para crear tu contraseña; tu cuenta queda lista al guardarla. Solo necesitas hacerlo una vez." />
          </ListItem>
          <ListItem>
            <ListItemIcon><Receipt color="primary" /></ListItemIcon>
            <ListItemText primary="2. Ingresa el número de tu ticket y el monto" secondary="El número aparece en tu ticket impreso (ej: H04550, U05434). El monto es el total del ticket." />
          </ListItem>
          <ListItem>
            <ListItemIcon><Schedule color="primary" /></ListItemIcon>
            <ListItemText primary="3. Tu factura se procesa automáticamente" secondary="Recibirás un correo con el PDF y XML cuando esté lista. También puedes descargarla desde este portal." />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* TICKETS PENDIENTES */}
        <Typography variant="h5" gutterBottom fontWeight="bold">Tickets pendientes</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Después de tu primera factura, el sistema identifica automáticamente tus otros tickets pendientes de facturar
          (del mes actual y el anterior). Los verás en la sección amarilla de "Tickets pendientes" y podrás facturarlos con un solo click.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* ESCENARIOS DE FECHA */}
        <Typography variant="h5" gutterBottom fontWeight="bold">Reglas de fecha</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          La fecha de tu compra determina cómo se procesa la factura:
        </Typography>

        <List dense>
          <ListItem>
            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
            <ListItemText
              primary="Compra de los últimos 3 días"
              secondary="La factura se emite con la fecha original de tu compra. Sin restricciones."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><Info color="info" /></ListItemIcon>
            <ListItemText
              primary="Compra de este mes, pero hace más de 3 días"
              secondary="La factura se emite con la fecha de hoy (no la de la compra). Esto es normal y cumple con las reglas del SAT."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><Warning color="warning" /></ListItemIcon>
            <ListItemText
              primary="Compra de un mes anterior"
              secondary="Requiere autorización del hospital. Te mostraremos un botón para solicitar por WhatsApp. Una vez autorizada, se procesa automáticamente."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* ESTADOS */}
        <Typography variant="h5" gutterBottom fontWeight="bold">Estados de factura</Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><Chip label="Pendiente" color="warning" size="small" /></ListItemIcon>
            <ListItemText primary="Tu solicitud fue recibida y se está procesando." />
          </ListItem>
          <ListItem>
            <ListItemIcon><Chip label="Facturada" color="success" size="small" /></ListItemIcon>
            <ListItemText primary="Factura lista. Puedes descargar PDF y XML, o reenviarla por correo." />
          </ListItem>
          <ListItem>
            <ListItemIcon><Chip label="Requiere autorización" color="warning" size="small" /></ListItemIcon>
            <ListItemText primary="Compra de mes anterior. Contacta por WhatsApp para autorización." />
          </ListItem>
          <ListItem>
            <ListItemIcon><Chip label="Error" color="error" size="small" /></ListItemIcon>
            <ListItemText primary="Hubo un problema. Verifica los datos o contacta al hospital." />
          </ListItem>
          <ListItem>
            <ListItemIcon><Chip label="Cancelada" size="small" /></ListItemIcon>
            <ListItemText primary="La factura fue cancelada ante el SAT." />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* CONTACTO */}
        <Typography variant="h5" gutterBottom fontWeight="bold">¿Necesitas ayuda?</Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><WhatsApp color="success" /></ListItemIcon>
            <ListItemText primary="WhatsApp: +52 999 184 1434" secondary="Para autorizaciones de meses anteriores, problemas con facturas, o dudas generales." />
          </ListItem>
          <ListItem>
            <ListItemIcon><Email color="primary" /></ListItemIcon>
            <ListItemText primary="También puedes solicitar tu factura directamente en recepción." />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* DATOS IMPORTANTES */}
        <Typography variant="h5" gutterBottom fontWeight="bold">Datos importantes</Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="• Tu RFC es tu identificador. Inicia sesión con tu RFC y contraseña." />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Si olvidaste tu contraseña o aún no tienes una, usa «¿Olvidaste tu contraseña o aún no tienes una?» en la entrada: te enviamos un enlace al correo de tu cuenta. Vale 60 minutos." />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Si ya no tienes acceso a ese correo, pide en recepción que lo cambien; luego pide el enlace otra vez." />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Un ticket solo se puede facturar una vez (a menos que se cancele la factura anterior)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Puedes ver tickets pendientes del mes actual y el anterior." />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Si cambias tus datos fiscales, ve a 'Datos fiscales' en el menú." />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Las facturas de meses anteriores requieren autorización del hospital." />
          </ListItem>
        </List>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <strong>Versión beta</strong> — Este sistema se encuentra en pruebas desde el 23 de marzo de 2026.
          Si encuentras algún error o tienes sugerencias, por favor comunícalo por WhatsApp al +52 999 184 1434.
        </Alert>
      </Paper>
    </Container>
  );
};
